import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, Loader2, PartyPopper, Star,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USE_CASE_OPTIONS = [
  "Gas", "Food", "Bills", "Rent", "Childcare",
  "Emergency Expense", "Vehicle Expense", "Phone Bill", "Other",
];

const ALTERNATIVE_OPTIONS = [
  "Waited until payout",
  "Borrowed from family or friends",
  "Used a credit card",
  "Taken out a payday loan",
  "Overdrafted my account",
  "Not paid the expense",
  "Other",
];

const FUTURE_AMOUNT_OPTIONS = ["$25", "$50", "$75", "$100", "$150", "$200+"];

const Stars = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        aria-label={`${n} star${n > 1 ? "s" : ""}`}
        data-testid={`pr-star-${n}`}
        className={`relative w-12 h-12 rounded-2xl border transition-all grid place-items-center ${
          value >= n
            ? "bg-[#00FF88]/15 border-[#00FF88]/50 text-[#00FF88]"
            : "bg-[#0A0A0A] border-white/10 text-white/30 hover:border-white/30"
        }`}
      >
        <Star className={`w-5 h-5 ${value >= n ? "fill-current" : ""}`} />
      </button>
    ))}
    <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/40 ml-2 w-full sm:w-auto">
      {value === 0 ? "Tap to rate" : value === 1 ? "Very unhelpful" : value === 5 ? "Extremely helpful" : `${value} / 5`}
    </div>
  </div>
);

const Pill = ({ active, onClick, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2.5 rounded-2xl border text-sm font-semibold transition-all ${
      active
        ? "bg-[#00FF88] text-black border-[#00FF88]"
        : "bg-[#0A0A0A] text-white/75 border-white/10 hover:border-white/30"
    }`}
  >
    {children}
  </button>
);

const Check2 = ({ active }) => (
  <span
    className={`w-5 h-5 rounded-md grid place-items-center border transition-colors ${
      active ? "bg-[#00FF88] border-[#00FF88]" : "bg-transparent border-white/30"
    }`}
  >
    {active && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
  </span>
);

const MultiCheck = ({ value, onToggle, options, testidPrefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
    {options.map((opt) => {
      const active = value.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onToggle(opt)}
          data-testid={`${testidPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-sm font-medium text-left transition-all ${
            active
              ? "bg-[#00FF88]/10 border-[#00FF88]/50 text-white"
              : "bg-[#0A0A0A] border-white/10 text-white/75 hover:border-white/30"
          }`}
        >
          <Check2 active={active} /> {opt}
        </button>
      );
    })}
  </div>
);

const SingleCheck = ({ value, onSelect, options, testidPrefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
    {options.map((opt) => {
      const active = value === opt;
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onSelect(opt)}
          data-testid={`${testidPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-sm font-medium text-left transition-all ${
            active
              ? "bg-[#00FF88]/10 border-[#00FF88]/50 text-white"
              : "bg-[#0A0A0A] border-white/10 text-white/75 hover:border-white/30"
          }`}
        >
          <span
            className={`w-5 h-5 rounded-full border grid place-items-center transition-colors ${
              active ? "border-[#00FF88]" : "border-white/30"
            }`}
          >
            {active && <span className="w-2.5 h-2.5 rounded-full bg-[#00FF88]" />}
          </span>
          {opt}
        </button>
      );
    })}
  </div>
);

const NPSScale = ({ value, onChange }) => (
  <div className="grid grid-cols-11 gap-1.5" data-testid="pr-nps">
    {Array.from({ length: 11 }, (_, n) => n).map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        data-testid={`pr-nps-${n}`}
        className={`h-12 rounded-xl border text-sm font-bold transition-all font-mono-c ${
          value === n
            ? "bg-[#00FF88] text-black border-[#00FF88]"
            : "bg-[#0A0A0A] text-white/70 border-white/10 hover:border-[#00FF88]/40"
        }`}
      >
        {n}
      </button>
    ))}
  </div>
);

const PostRepayment = () => {
  const { appId } = useParams();
  const [stage, setStage] = useState("verify"); // verify | success | survey | done | locked
  const [eligible, setEligible] = useState(null);
  const [email, setEmail] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({
    helpfulness_rating: 0,
    float_use_case: [],
    alternative_option: "",
    would_use_again: "",
    future_float_amount: "",
    nps_score: -1,
    improvement_feedback: "",
    testimonial_permission: "",
    future_beta_interest: "",
  });

  const toggleUseCase = (opt) => {
    setForm((f) => ({
      ...f,
      float_use_case: f.float_use_case.includes(opt)
        ? f.float_use_case.filter((x) => x !== opt)
        : [...f.float_use_case, opt],
    }));
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter the email you used on your application.");
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.get(`${API}/pilot/post-repayment/check/${appId}`, { params: { email } });
      const data = res.data;
      if (data.eligible) {
        setEligible(data);
        setStage("success");
      } else if (data.reason === "already_submitted") {
        setEligible(data);
        setStage("locked");
      } else if (data.reason === "not_repaid") {
        toast.error("This survey unlocks after your Float is repaid. Hang tight!");
      } else {
        toast.error("We couldn't find a repaid application with that email.");
      }
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setVerifying(false);
    }
  };

  const valid =
    form.helpfulness_rating >= 1 && form.helpfulness_rating <= 5 &&
    form.float_use_case.length >= 1 &&
    !!form.alternative_option &&
    !!form.would_use_again &&
    !!form.future_float_amount &&
    form.nps_score >= 0 && form.nps_score <= 10 &&
    form.improvement_feedback.trim().length >= 1 &&
    !!form.testimonial_permission &&
    !!form.future_beta_interest;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please answer every question — they're all required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/pilot/post-repayment/${appId}`, { email, ...form });
      const res = await axios.get(`${API}/pilot/post-repayment/summary`);
      setSummary(res.data);
      setStage("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  // Preload summary stats once the user lands on the success screen so the
  // final screen shows them instantly after submit.
  useEffect(() => {
    if (stage === "success" && !summary) {
      axios.get(`${API}/pilot/post-repayment/summary`)
        .then((r) => setSummary(r.data))
        .catch(() => { /* non-fatal */ });
    }
  }, [stage, summary]);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="post-repayment-page">
      <header className="border-b border-white/5 sticky top-0 z-40 bg-[#050505]/70 backdrop-blur-xl">
        <div className="max-w-[760px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4 text-white/60" />
            <LogoMark size={28} />
            <span className="font-display font-extrabold text-base">GigAfloat</span>
          </Link>
          <span className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88]">
            // Post-repayment
          </span>
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-6 lg:px-12 py-10 sm:py-14">
        {stage === "verify" && (
          <>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-3">// Verify your application</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-2">
              Welcome back.
            </h1>
            <p className="text-white/60 mb-8 leading-relaxed">
              Enter the email you used on your Founder Pilot application to unlock your post-repayment survey.
            </p>
            <form onSubmit={verify} className="space-y-4" data-testid="pr-verify-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim())}
                placeholder="you@example.com"
                className="w-full px-4 py-3.5 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20"
                data-testid="pr-verify-email"
              />
              <button
                type="submit"
                disabled={verifying}
                className="w-full sm:w-auto inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-base px-7 py-3.5 rounded-full btn-glow disabled:opacity-50"
                data-testid="pr-verify-submit"
              >
                {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-5 h-5" /></>}
              </button>
            </form>
          </>
        )}

        {stage === "locked" && (
          <div className="text-center py-12" data-testid="pr-locked">
            <PartyPopper className="w-12 h-12 mx-auto text-[#00FF88] mb-5" />
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-3">
              Already submitted.
            </h1>
            <p className="text-white/60 leading-relaxed mb-8">
              We&apos;ve already received your post-repayment survey{eligible?.first_name ? `, ${eligible.first_name}` : ""}. Thank you for being part of the pilot.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-6 py-3 rounded-full btn-glow">
              Back to GigAfloat <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {stage === "success" && (
          <div className="text-center py-8 sm:py-12" data-testid="pr-success">
            <div className="relative inline-grid place-items-center mb-7">
              <div className="absolute inset-0 rounded-full bg-[#00FF88]/25 blur-3xl scale-150" />
              <div className="relative w-24 h-24 rounded-full bg-[#0A0A0A] border-2 border-[#00FF88] grid place-items-center gf-feature-card">
                <span className="text-5xl" aria-hidden="true">💚</span>
              </div>
            </div>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-3">// Float Pilot</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-4">
              Float Repaid Successfully
            </h1>
            <p className="text-white/65 leading-relaxed max-w-xl mx-auto mb-3">
              Thank you for being part of the GigAfloat Founder Pilot{eligible?.first_name ? `, ${eligible.first_name}` : ""}.
            </p>
            <p className="text-white/55 leading-relaxed max-w-xl mx-auto mb-10">
              Your feedback helps us build a better financial continuity platform for gig workers.
            </p>
            <button
              type="button"
              onClick={() => setStage("survey")}
              className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-base px-8 py-4 rounded-full btn-glow"
              data-testid="pr-continue-btn"
            >
              Continue <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {stage === "survey" && (
          <>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-3">// 9 questions</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-2">
              Hey {eligible?.first_name || "there"} — how did your Float go?
            </h1>
            <p className="text-white/55 mb-9 leading-relaxed">All 9 questions are required. Takes about ~90 seconds.</p>

            <form onSubmit={submit} className="space-y-9" data-testid="pr-form">
              {/* Q1 */}
              <section data-testid="pr-q1">
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Q1 · How helpful was your Float?</div>
                <Stars value={form.helpfulness_rating} onChange={(v) => setForm((f) => ({ ...f, helpfulness_rating: v }))} testid="pr-stars" />
              </section>

              {/* Q2 */}
              <section data-testid="pr-q2">
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Q2 · What did your Float help you cover? <span className="text-white/30">(Select all that apply)</span></div>
                <MultiCheck value={form.float_use_case} onToggle={toggleUseCase} options={USE_CASE_OPTIONS} testidPrefix="pr-use" />
              </section>

              {/* Q3 */}
              <section data-testid="pr-q3">
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Q3 · Without GigAfloat, what would you have done?</div>
                <SingleCheck value={form.alternative_option} onSelect={(v) => setForm((f) => ({ ...f, alternative_option: v }))} options={ALTERNATIVE_OPTIONS} testidPrefix="pr-alt" />
              </section>

              {/* Q4 */}
              <section data-testid="pr-q4">
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Q4 · Would you use GigAfloat again?</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["yes", "Yes"], ["no", "No"], ["maybe", "Maybe"]].map(([v, l]) => (
                    <Pill key={v} active={form.would_use_again === v} onClick={() => setForm((f) => ({ ...f, would_use_again: v }))} testid={`pr-again-${v}`}>{l}</Pill>
                  ))}
                </div>
              </section>

              {/* Q5 */}
              <section data-testid="pr-q5">
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Q5 · What Float amount would be most helpful in the future?</div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {FUTURE_AMOUNT_OPTIONS.map((amt) => (
                    <Pill key={amt} active={form.future_float_amount === amt} onClick={() => setForm((f) => ({ ...f, future_float_amount: amt }))} testid={`pr-amt-${amt.toLowerCase().replace(/[^a-z0-9]+/g, "")}`}>{amt}</Pill>
                  ))}
                </div>
              </section>

              {/* Q6 */}
              <section data-testid="pr-q6">
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Q6 · How likely are you to recommend GigAfloat to another gig worker? <span className="text-white/30">(0–10)</span></div>
                <NPSScale value={form.nps_score} onChange={(v) => setForm((f) => ({ ...f, nps_score: v }))} />
                <div className="flex justify-between mt-2 font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/40">
                  <span>Not likely</span>
                  <span>Extremely likely</span>
                </div>
              </section>

              {/* Q7 */}
              <section data-testid="pr-q7">
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Q7 · What is one thing we could improve?</div>
                <textarea
                  rows={4}
                  value={form.improvement_feedback}
                  onChange={(e) => setForm((f) => ({ ...f, improvement_feedback: e.target.value.slice(0, 2000) }))}
                  placeholder="Be honest — this shapes what we build next."
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 resize-y text-sm"
                  data-testid="pr-improvement"
                />
              </section>

              {/* Q8 */}
              <section data-testid="pr-q8">
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Q8 · Can we contact you for a customer success story or testimonial?</div>
                <div className="grid grid-cols-2 gap-2">
                  {[["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
                    <Pill key={v} active={form.testimonial_permission === v} onClick={() => setForm((f) => ({ ...f, testimonial_permission: v }))} testid={`pr-testimonial-${v}`}>{l}</Pill>
                  ))}
                </div>
              </section>

              {/* Q9 */}
              <section data-testid="pr-q9">
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Q9 · Would you like early access to future GigAfloat features?</div>
                <div className="grid grid-cols-2 gap-2">
                  {[["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
                    <Pill key={v} active={form.future_beta_interest === v} onClick={() => setForm((f) => ({ ...f, future_beta_interest: v }))} testid={`pr-beta-${v}`}>{l}</Pill>
                  ))}
                </div>
              </section>

              <button
                type="submit"
                disabled={loading || !valid}
                className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-base px-8 py-4 rounded-full btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pr-submit"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send my responses <Check className="w-5 h-5" /></>}
              </button>
            </form>
          </>
        )}

        {stage === "done" && (
          <div className="text-center py-8 sm:py-12" data-testid="pr-done">
            <div className="text-6xl mb-5">🎉</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-3">
              Thank You
            </h1>
            <p className="text-white/65 leading-relaxed max-w-xl mx-auto mb-10">
              Your feedback helps us build GigAfloat for the millions of workers who keep the world moving.
            </p>

            {summary && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-10" data-testid="pr-summary">
                <div className="rounded-2xl bg-[#0A0A0A] border border-[#00FF88]/20 p-5">
                  <div className="font-mono-c text-[10px] uppercase tracking-[0.22em] text-[#00FF88] mb-2">Total Floats Repaid</div>
                  <div className="font-mono-c font-bold text-3xl text-white">{summary.total_floats_repaid ?? "—"}</div>
                </div>
                <div className="rounded-2xl bg-[#0A0A0A] border border-[#00FF88]/20 p-5">
                  <div className="font-mono-c text-[10px] uppercase tracking-[0.22em] text-[#00FF88] mb-2">Pilot Participants</div>
                  <div className="font-mono-c font-bold text-3xl text-white">{summary.total_pilot_participants ?? "—"}</div>
                </div>
                <div className="rounded-2xl bg-[#0A0A0A] border border-[#00FF88]/20 p-5">
                  <div className="font-mono-c text-[10px] uppercase tracking-[0.22em] text-[#00FF88] mb-2">Avg Survey Rating</div>
                  <div className="font-mono-c font-bold text-3xl text-white">
                    {summary.average_survey_rating ?? "—"}<span className="text-white/40 text-lg"> / 5</span>
                  </div>
                </div>
              </div>
            )}

            <Link to="/" className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-6 py-3 rounded-full btn-glow" data-testid="pr-back-home">
              Back to GigAfloat <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default PostRepayment;
